import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Plus, Search, Timer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import CreateTicketModal from '../components/CreateTicketModal';
import TicketDetailModal from '../components/TicketDetailModal';


function Tickets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [boards, setBoards] = useState([]);
  const [activeFilter, setActiveFilter] = useState('my_pending');

  useEffect(() => {
    // Get filter from URL params
    const filterParam = searchParams.get('filter');
    if (filterParam && ['my_total', 'my_pending', 'my_high_priority', 'my_completed', 'created_by_me', 'assigned_to_me'].includes(filterParam)) {
      setActiveFilter(filterParam);
    }
  }, [searchParams]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ticketsRes, boardsRes] = await Promise.all([
        api.get(`/tickets?filter_type=${activeFilter}`),
        api.get(`/boards`),
      ]);
      setTickets(ticketsRes.data);
      setBoards(boardsRes.data);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  const filterTickets = useCallback(() => {
    let filtered = tickets;

    if (searchQuery) {
      filtered = filtered.filter(
        (t) =>
          t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter((t) => t.priority === priorityFilter);
    }

    setFilteredTickets(filtered);
  }, [tickets, searchQuery, priorityFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    filterTickets();
  }, [filterTickets]);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setSearchParams({ filter });
  };

  const handleTicketCreated = (newTicket) => {
    setTickets([newTicket, ...tickets]);
    setShowCreateTicket(false);
  };

  const handleTicketUpdated = (updatedTicket) => {
    setTickets(tickets.map((t) => (t.ticket_id === updatedTicket.ticket_id ? updatedTicket : t)));
    setSelectedTicket(null);
  };

  const handleTicketDeleted = (ticketId) => {
    setTickets(tickets.filter((t) => t.ticket_id !== ticketId));
    setSelectedTicket(null);
  };

  const formatTime = (minutes) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  const filterLabels = {
    my_total: 'All My Tickets',
    my_pending: 'My Pending',
    my_high_priority: 'High Priority',
    my_completed: 'Completed',
    created_by_me: 'Created by Me',
    assigned_to_me: 'Assigned to Me'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto p-6" data-testid="tickets-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-stone-900 mb-2">My Tickets</h1>
            <p className="text-sm text-stone-600 leading-relaxed">
              Manage and track tickets you've created or been assigned
            </p>
          </div>
          <Button
            data-testid="create-ticket-button"
            onClick={() => setShowCreateTicket(true)}
            className="bg-primary hover:bg-primary-hover text-stone-900 rounded-xl shadow-gold font-semibold transition-all hover:-translate-y-0.5 active:translate-y-0"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
            New Ticket
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(filterLabels).map(([key, label]) => (
            <button
              key={key}
              data-testid={`filter-tab-${key}`}
              onClick={() => handleFilterChange(key)}
              className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                activeFilter === key
                  ? 'bg-primary text-stone-900 shadow-gold'
                  : 'bg-white border border-stone-200 text-stone-700 hover:bg-stone-50 hover:border-primary/30'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Search and Priority Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400 w-5 h-5"
              strokeWidth={1.5}
            />
            <Input
              data-testid="search-tickets-input"
              type="text"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl border-stone-200 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'high', 'medium', 'low'].map((priority) => (
              <button
                key={priority}
                data-testid={`priority-filter-${priority}`}
                onClick={() => setPriorityFilter(priority)}
                className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                  priorityFilter === priority
                    ? 'bg-stone-900 text-white'
                    : 'bg-white border border-stone-200 text-stone-700 hover:bg-stone-50'
                }`}
              >
                {priority === 'all' ? 'All' : priority.charAt(0).toUpperCase() + priority.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Assignee
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Timer className="w-3.5 h-3.5" strokeWidth={1.5} />
                    TTC
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Created By
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {filteredTickets.map((ticket) => (
                <tr
                  key={ticket.ticket_id}
                  data-testid={`ticket-row-${ticket.ticket_id}`}
                  onClick={() => setSelectedTicket(ticket)}
                  className="hover:bg-stone-50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-stone-800">{ticket.title}</p>
                      {ticket.description && (
                        <p className="text-sm text-stone-500 mt-1 line-clamp-1">
                          {ticket.description}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-3 py-1 rounded-full text-xs font-medium border ${
                        ticket.priority === 'high'
                          ? 'bg-rose-50 text-rose-700 border-rose-200'
                          : ticket.priority === 'medium'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-stone-100 text-stone-600 border-stone-200'
                      }`}
                    >
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-600">
                    {ticket.assignee_name || <span className="text-stone-400">Unassigned</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-600">
                    {ticket.due_date
                      ? new Date(ticket.due_date + 'T12:00:00').toLocaleDateString()
                      : 'No due date'}
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-600">
                    {ticket.time_to_complete ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                        <Timer className="w-3 h-3" strokeWidth={1.5} />
                        {formatTime(ticket.time_to_complete)}
                      </span>
                    ) : (
                      <span className="text-stone-400">--</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-600">
                    {ticket.created_by_name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredTickets.length === 0 && (
            <div className="text-center py-12 text-stone-500">
              <p>
                {searchQuery || priorityFilter !== 'all'
                  ? 'No tickets match your filters'
                  : 'No tickets found'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showCreateTicket && (
        <CreateTicketModal
          boards={boards}
          onClose={() => setShowCreateTicket(false)}
          onTicketCreated={handleTicketCreated}
        />
      )}

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          boards={boards}
          onClose={() => setSelectedTicket(null)}
          onTicketUpdated={handleTicketUpdated}
          onTicketDeleted={handleTicketDeleted}
        />
      )}
    </div>
  );
}

export default Tickets;
